import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { CaseTypeEntity } from '../../domain/entities/case-type.entity';
import { CaseTypeDetailEntity } from '../../domain/entities/case-type-detail.entity';
import { CaseTypeCategoryEntity } from '../../domain/entities/case-type-category.entity';
import { CaseTypeDeletionImpactEntity } from '../../domain/entities/case-type-deletion-impact.entity';
import { ClosedCaseVisibility } from '../../domain/enums/closed-case-visibility.enum';
import { CaseTypeApi } from '../api/case-type.api';
import { CaseTypeMapper } from '../mappers/case-type.mapper';
import { GridBaseQuery } from '@core';

export interface CreateCaseTypeParams {
  name: string;
  description: string;
  categoryId: string;
  isActive: boolean;
  sortOrder: number;
  closedCaseVisibility: ClosedCaseVisibility;
}

export interface UpdateCaseTypeParams {
  name: string;
  description: string;
  categoryId: string;
  isActive: boolean;
  sortOrder: number;
  closedCaseVisibility: ClosedCaseVisibility;
}

/**
 * CaseType Repository
 * Handles all case type data operations through the API layer.
 */
@Injectable({ providedIn: 'root' })
export class CaseTypeRepository {
  private readonly _api = inject(CaseTypeApi);
  private readonly _mapper = inject(CaseTypeMapper);

  public getAllCaseTypes(): Observable<CaseTypeEntity[]> {
    return this._api.getAllCaseTypes().pipe(map((result) => this._mapper.toDomainArray(result.data)));
  }

  public getActiveCaseTypes(): Observable<CaseTypeEntity[]> {
    return this._api.getActiveCaseTypes().pipe(map((dtos) => this._mapper.toDomainArray(dtos)));
  }

  public getCaseTypesWithPaging(query: GridBaseQuery): Observable<{ data: CaseTypeEntity[]; totalElements: number }> {
    return this._api.getCaseTypesWithPaging(query).pipe(
      map((result) => ({
        data: (result.data || []).map((dto) => this._mapper.toDomain(dto)),
        totalElements: result.totalElements || 0,
      })),
    );
  }

  public getCaseTypeById(id: string): Observable<CaseTypeDetailEntity> {
    return this._api.getCaseTypeById(id).pipe(map((dto) => this._mapper.toDetailDomain(dto)));
  }

  public getCategories(): Observable<CaseTypeCategoryEntity[]> {
    return this._api.getCategories().pipe(map((dtos) => this._mapper.toCategoryDomainArray(dtos)));
  }

  public createCaseType(params: CreateCaseTypeParams): Observable<{ id: string }> {
    return this._api.createCaseType({
      name: params.name,
      description: params.description,
      categoryId: params.categoryId,
      isActive: params.isActive,
      sortOrder: params.sortOrder,
      closedCaseVisibility: params.closedCaseVisibility,
    });
  }

  public updateCaseType(id: string, params: UpdateCaseTypeParams): Observable<void> {
    return this._api.updateCaseType(id, {
      name: params.name,
      description: params.description,
      categoryId: params.categoryId,
      isActive: params.isActive,
      sortOrder: params.sortOrder,
      closedCaseVisibility: params.closedCaseVisibility,
    });
  }

  public deleteCaseType(id: string): Observable<void> {
    return this._api.deleteCaseType(id);
  }

  public toggleCaseTypeActive(id: string, isActive: boolean): Observable<void> {
    return this._api.toggleCaseTypeActive(id, { isActive });
  }

  public getCaseTypeDeletionImpact(id: string): Observable<CaseTypeDeletionImpactEntity> {
    return this._api.getCaseTypeDeletionImpact(id).pipe(map((dto) => this._mapper.toDeletionImpactDomain(dto)));
  }
}
